import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, CalendarDays, List, Plus, Search, Eye, Pencil, Trash2,
  Users, MapPin, Clock, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import {
  getRepEvents,
  getRepEventTypes,
  deleteRepEvent,
  getMyProfile,
  removeRepresentativeToken
} from '../../services/representativeService';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import ConfirmDialog from '../../components/associative/AssocConfirmDialog';

interface EventType {
  _id: string;
  nome: string;
  colore: string;
}

interface RepEvent {
  _id: string;
  nome: string;
  tipologiaId: { _id: string; nome: string; colore: string } | null;
  authorRepresentativeId: { _id: string; nome: string; cognome: string } | null;
  dataEvento: string;
  oraInizio: string;
  oraFine: string;
  luogo: string;
  stato: string;
  invitationCount: number;
  checkedInCount: number;
  isOwner: boolean;
  settore: string;
  enableQrCode: boolean;
}

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string;
  associatesAccess?: boolean;
  permissions: { name: string; description: string }[];
}

const STATO_OPTIONS = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'BOZZA', label: 'Bozza' },
  { value: 'PUBBLICATO', label: 'Pubblicato' },
  { value: 'IN_CORSO', label: 'In corso' },
  { value: 'CONCLUSO', label: 'Concluso' },
  { value: 'ANNULLATO', label: 'Annullato' },
];

// Mappatura stato → classe badge del design system .assoc-app.
// Valori non coperti 1:1 (ANNULLATO) usano colore inline coerente (rosso).
const STATO_BADGE_CLASS: Record<string, string> = {
  BOZZA: 's-bozza',
  PUBBLICATO: 's-programmato',
  IN_CORSO: 's-inviato',
  CONCLUSO: 's-concluso',
};

const statoBadge = (stato: string): { className: string; style?: React.CSSProperties } => {
  if (stato === 'ANNULLATO') return { className: '', style: { background: '#FBEDEB', color: '#B23A2C' } };
  return { className: STATO_BADGE_CLASS[stato] || 's-bozza' };
};

const STATO_LABEL: Record<string, string> = {
  BOZZA: 'Bozza',
  PUBBLICATO: 'Pubblicato',
  IN_CORSO: 'In corso',
  CONCLUSO: 'Concluso',
  ANNULLATO: 'Annullato',
};

const MESI_SHORT = ['GEN', 'FEB', 'MAR', 'APR', 'MAG', 'GIU', 'LUG', 'AGO', 'SET', 'OTT', 'NOV', 'DIC'];

const dateChipParts = (dateStr: string): { m: string; g: string } => {
  try {
    const d = new Date(dateStr);
    return { m: MESI_SHORT[d.getMonth()] || '', g: String(d.getDate()).padStart(2, '0') };
  } catch {
    return { m: '', g: '--' };
  }
};

const AssociativeCalendar: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [events, setEvents] = useState<RepEvent[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ tipologiaId: '', stato: '', search: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [profile, setProfile] = useState<RepProfile | null>(null);

  // Vista lista vs calendario mensile
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const now = new Date();
  const [calMonth, setCalMonth] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [calEvents, setCalEvents] = useState<RepEvent[]>([]);
  const [calLoading, setCalLoading] = useState(false);

  // 403 dalle rotte degli eventi: la carica non ha associativa.eventi.read.
  // Non è una sessione scaduta: si resta nell'area con un avviso, senza login.
  const [senzaPermesso, setSenzaPermesso] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<RepEvent | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canCreate = profile?.permissions?.some(p => p.name === 'associativa.eventi.create') ?? false;
  const canLive = profile?.permissions?.some(p => p.name === 'associativa.live.publish') ?? false;
  const canCircolari = profile?.permissions?.some(p => p.name === 'associativa.circolari.read') ?? false;
  const canSondaggi = profile?.permissions?.some(p => p.name === 'associativa.sondaggi.read') ?? false;
  const canCalendario = profile?.permissions?.some(p => p.name === 'associativa.eventi.read') ?? false;

  // Load profile
  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }

    const loadProfile = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) {
          setProfile(res.data);
        }
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      }
    };
    loadProfile();
  }, [navigate, toast]);

  // Load event types
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await getRepEventTypes();
        if (res.success) {
          setEventTypes(res.data);
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        if (err.response?.status === 403) setSenzaPermesso(true);
      }
    };
    loadTypes();
  }, [navigate]);

  // Load events
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {
        page: page.toString(),
        limit: '20',
      };
      if (filters.tipologiaId) params.tipologiaId = filters.tipologiaId;
      if (filters.stato) params.stato = filters.stato;
      if (filters.search) params.search = filters.search;

      const res = await getRepEvents(params);
      if (res.success) {
        setEvents(res.data);
        setTotalPages(res.totalPages || 1);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        removeRepresentativeToken();
        navigate('/accesso-associati');
        return;
      }
      if (err.response?.status === 403) {
        setSenzaPermesso(true);
        return;
      }
      toast.error('Errore caricamento eventi');
    } finally {
      setLoading(false);
    }
  }, [page, filters, navigate, toast]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters.tipologiaId, filters.stato, filters.search]);

  // Carica gli eventi per la vista calendario (fetch ampio, raggruppato per
  // giorno lato client: l'API non filtra per intervallo di date)
  const fetchCalEvents = useCallback(async () => {
    try {
      setCalLoading(true);
      const params: Record<string, string> = { page: '1', limit: '500' };
      if (filters.tipologiaId) params.tipologiaId = filters.tipologiaId;
      if (filters.stato) params.stato = filters.stato;
      if (filters.search) params.search = filters.search;
      const res = await getRepEvents(params);
      if (res.success) setCalEvents(res.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        removeRepresentativeToken();
        navigate('/accesso-associati');
        return;
      }
      if (err.response?.status === 403) {
        setSenzaPermesso(true);
        return;
      }
      toast.error('Errore caricamento calendario');
    } finally {
      setCalLoading(false);
    }
  }, [filters, navigate, toast]);

  useEffect(() => {
    if (viewMode === 'calendar') fetchCalEvents();
  }, [viewMode, fetchCalEvents]);

  // Delete event
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteRepEvent(deleteTarget._id);
      if (res.success) {
        toast.success('Evento eliminato');
        fetchEvents();
      }
    } catch (err: any) {
      toast.error('Errore eliminazione evento');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ tipologiaId: '', stato: '', search: '' });
  };

  const activeFiltersCount = [filters.tipologiaId, filters.stato, filters.search].filter(Boolean).length;

  return (
    <AssociativeAppShell
      route="calendario"
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={profile?.caricaPrincipale}
      tipo={profile?.tipo}
      foto={profile?.foto}
      associatesAccess={profile?.associatesAccess}
      canLive={canLive}
      canCircolari={canCircolari}
      canSondaggi={canSondaggi}
      canCalendario={canCalendario}
    >
      {senzaPermesso ? (
        <div className="page fade-in">
          <section className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px' }}>
              <Calendar size={28} className="muted" style={{ display: 'block', margin: '0 auto 10px' }} />
              <div style={{ fontSize: '14px', fontWeight: 600 }}>Calendario non disponibile</div>
              <div className="muted" style={{ fontSize: '13px', marginTop: '6px' }}>
                La tua carica non prevede l'accesso al Calendario Attività.
              </div>
              <button className="btn primary sm" type="button" style={{ marginTop: '16px' }} onClick={() => navigate('/area-associativa')}>
                Torna alla dashboard
              </button>
            </div>
          </section>
        </div>
      ) : (
      <div className="page fade-in">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '22px' }}>
          <div>
            <div className="mono-label" style={{ color: 'var(--un-green-700)' }}>Area Rappresentativa</div>
            <h1 style={{ margin: '4px 0 0', font: '700 26px var(--font-display)', letterSpacing: 'var(--tracking-tight)' }}>
              Calendario Attività
            </h1>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div className="seg" role="group" aria-label="Vista">
              <button type="button" aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}>
                <List size={14} /> Lista
              </button>
              <button type="button" aria-pressed={viewMode === 'calendar'} onClick={() => setViewMode('calendar')}>
                <CalendarDays size={14} /> Mese
              </button>
            </div>
            {canCreate && (
              <button className="btn primary" type="button" onClick={() => navigate('/area-associativa/calendario/nuovo')}>
                <Plus size={15} /> Nuovo Evento
              </button>
            )}
          </div>
        </div>

        {/* Filtri */}
        <div className="toolbar" style={{ marginBottom: '16px' }}>
          <div className="search">
            <Search size={15} />
            <input
              type="text"
              placeholder="Cerca evento…"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <select
            className="select-pill"
            value={filters.tipologiaId}
            onChange={(e) => handleFilterChange('tipologiaId', e.target.value)}
          >
            <option value="">Tutte le tipologie</option>
            {eventTypes.map(t => (
              <option key={t._id} value={t._id}>{t.nome}</option>
            ))}
          </select>
          <select
            className="select-pill"
            value={filters.stato}
            onChange={(e) => handleFilterChange('stato', e.target.value)}
          >
            {STATO_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {activeFiltersCount > 0 && (
            <button className="top-link" type="button" onClick={clearFilters} title="Rimuovi filtri">
              <X size={14} /> Pulisci
            </button>
          )}
        </div>

        {/* ===== CALENDAR VIEW (griglia mensile) ===== */}
        {viewMode === 'calendar' && (() => {
          const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
          const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
          const firstDay = new Date(calMonth.year, calMonth.month, 1);
          const lastDay = new Date(calMonth.year, calMonth.month + 1, 0);
          const startDow = (firstDay.getDay() + 6) % 7; // 0=Lun
          const totalDays = lastDay.getDate();
          const today = new Date();
          const isToday = (d: number) => today.getFullYear() === calMonth.year && today.getMonth() === calMonth.month && today.getDate() === d;

          const eventsByDay: Record<number, RepEvent[]> = {};
          calEvents.forEach(ev => {
            const d = new Date(ev.dataEvento);
            if (d.getMonth() === calMonth.month && d.getFullYear() === calMonth.year) {
              const day = d.getDate();
              if (!eventsByDay[day]) eventsByDay[day] = [];
              eventsByDay[day].push(ev);
            }
          });

          // Matrice settimane (7 colonne, null per le celle fuori mese)
          const weeks: (number | null)[][] = [];
          let week: (number | null)[] = [];
          for (let i = 0; i < startDow; i++) week.push(null);
          for (let d = 1; d <= totalDays; d++) {
            week.push(d);
            if (week.length === 7) { weeks.push(week); week = []; }
          }
          if (week.length) { while (week.length < 7) week.push(null); weeks.push(week); }

          return (
            <section className="card">
              <div className="card-head" style={{ borderBottom: 'none', paddingBottom: '6px' }}>
                <button
                  className="top-link"
                  type="button"
                  style={{ padding: '6px' }}
                  onClick={() => setCalMonth(prev => prev.month === 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: prev.month - 1 })}
                  aria-label="Mese precedente"
                >
                  <ChevronLeft size={15} />
                </button>
                <h2 style={{ flex: 1, textAlign: 'center', fontSize: '16px' }}>
                  {MESI[calMonth.month]} {calMonth.year}
                </h2>
                <button
                  className="top-link"
                  type="button"
                  style={{ padding: '6px' }}
                  onClick={() => setCalMonth(prev => prev.month === 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: prev.month + 1 })}
                  aria-label="Mese successivo"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
              <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
                {calLoading ? (
                  <div className="muted" style={{ textAlign: 'center', padding: '48px', fontSize: '13px' }}>Caricamento…</div>
                ) : (
                  <table className="cal">
                    <thead>
                      <tr>{GIORNI.map(g => <th key={g} scope="col">{g}</th>)}</tr>
                    </thead>
                    <tbody>
                      {weeks.map((w, wi) => (
                        <tr key={wi}>
                          {w.map((d, di) => (
                            <td key={di} className={d === null ? 'off' : isToday(d) ? 'today' : ''}>
                              {d !== null && <span className="dnum">{d}</span>}
                              {d !== null && (eventsByDay[d] || []).map(ev => (
                                <button
                                  key={ev._id}
                                  type="button"
                                  className="ev"
                                  style={ev.tipologiaId ? { background: ev.tipologiaId.colore + '20', color: ev.tipologiaId.colore } : undefined}
                                  onClick={() => navigate(`/area-associativa/calendario/${ev._id}`)}
                                  title={`${ev.nome}${ev.oraInizio ? ' · ' + ev.oraInizio : ''}`}
                                >
                                  {ev.oraInizio ? `${ev.oraInizio} · ` : ''}{ev.nome}
                                </button>
                              ))}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          );
        })()}

        {/* ===== LIST VIEW ===== */}
        {viewMode === 'list' && (loading ? (
          <section className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px' }}>
              <div className="muted" style={{ fontSize: '13px' }}>Caricamento…</div>
            </div>
          </section>
        ) : events.length === 0 ? (
          /* Empty state */
          <section className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '48px' }}>
              <Calendar size={28} className="muted" style={{ display: 'block', margin: '0 auto 10px' }} />
              <div className="muted" style={{ fontSize: '13px' }}>Nessun evento in calendario</div>
              {canCreate && (
                <button
                  className="btn primary sm"
                  type="button"
                  style={{ marginTop: '16px' }}
                  onClick={() => navigate('/area-associativa/calendario/nuovo')}
                >
                  <Plus size={14} /> Crea il primo evento
                </button>
              )}
            </div>
          </section>
        ) : (
          <section className="card">
            {events.map(ev => {
              const chip = dateChipParts(ev.dataEvento);
              const stato = statoBadge(ev.stato);
              return (
                <div
                  key={ev._id}
                  className="event-row"
                  onClick={() => navigate(`/area-associativa/calendario/${ev._id}`)}
                >
                  <div className="date-chip">
                    <span className="m">{chip.m}</span>
                    <span className="g">{chip.g}</span>
                  </div>

                  <div className="info">
                    <div className="t">
                      {ev.nome}
                      <span className={`badge ${stato.className}`} style={stato.style}>
                        {STATO_LABEL[ev.stato] || ev.stato}
                      </span>
                    </div>
                    <div className="meta">
                      {ev.tipologiaId && (
                        <span
                          className="badge"
                          style={{ background: ev.tipologiaId.colore + '20', color: ev.tipologiaId.colore }}
                        >
                          {ev.tipologiaId.nome}
                        </span>
                      )}
                      {(ev.oraInizio || ev.oraFine) && (
                        <span>
                          <Clock size={13} /> {ev.oraInizio}{ev.oraFine ? ` - ${ev.oraFine}` : ''}
                        </span>
                      )}
                      {ev.luogo && (
                        <span>
                          <MapPin size={13} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>{ev.luogo}</span>
                        </span>
                      )}
                      {ev.authorRepresentativeId && !ev.isOwner && (
                        <span>di {ev.authorRepresentativeId.nome} {ev.authorRepresentativeId.cognome}</span>
                      )}
                    </div>
                  </div>

                  <div className="right" onClick={(e) => e.stopPropagation()}>
                    <span className="inv" title="Invitati">
                      <Users size={14} /> {ev.invitationCount}
                    </span>
                    <button
                      className="top-link"
                      type="button"
                      style={{ padding: '6px' }}
                      title="Visualizza"
                      onClick={() => navigate(`/area-associativa/calendario/${ev._id}`)}
                    >
                      <Eye size={14} />
                    </button>
                    {ev.isOwner && (
                      <>
                        <button
                          className="top-link"
                          type="button"
                          style={{ padding: '6px' }}
                          title="Modifica"
                          onClick={() => navigate(`/area-associativa/calendario/${ev._id}/modifica`)}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          className="top-link exit"
                          type="button"
                          style={{ padding: '6px' }}
                          title="Elimina"
                          onClick={() => setDeleteTarget(ev)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                    <ChevronRight className="chev" size={16} />
                  </div>
                </div>
              );
            })}
          </section>
        ))}

        {/* Pagination (solo in vista lista) */}
        {viewMode === 'list' && !loading && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft size={14} /> Precedente
            </button>
            <span className="muted" style={{ fontSize: '12.5px' }}>
              Pagina {page} di {totalPages}
            </span>
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Successiva <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Elimina evento"
        message={`Sei sicuro di voler eliminare l'evento "${deleteTarget?.nome}"? L'azione non e reversibile.`}
        confirmText={deleting ? 'Eliminazione...' : 'Elimina'}
        type="danger"
      />
    </AssociativeAppShell>
  );
};

export default AssociativeCalendar;
